import { useMemo, useEffect, useState } from "react";
import { type Device } from "@/hooks/useDevices";
import { calculateDeviceHealthScore, saveDeviceHealthScore, getHealthScoreTrend, type HealthTrend } from "@/utils/deviceHealth";
import { HealthScoreCard } from "./health/HealthScoreCard";
import { HealthMetricCard } from "./health/HealthMetricCard";
import { HealthInsightsPanel } from "./health/HealthInsightsPanel";

interface DeviceAnalyticsProps {
  device: Device;
}

const DeviceAnalytics = ({ device }: DeviceAnalyticsProps) => {
  const healthScore = useMemo(() => calculateDeviceHealthScore(device), [device]);
  const [healthTrend, setHealthTrend] = useState<HealthTrend | null>(null);
  const [isLoadingTrend, setIsLoadingTrend] = useState(true);

  useEffect(() => {
    const loadHealthData = async () => {
      try {
        // Save current health score
        await saveDeviceHealthScore(device.id, healthScore);
        
        // Fetch historical trend (compare with 7 days ago)
        const trend = await getHealthScoreTrend(device.id, 7);
        setHealthTrend(trend);
      } catch (error) {
        console.error('Error loading health data:', error);
      } finally {
        setIsLoadingTrend(false);
      }
    };

    loadHealthData();
  }, [device.id, healthScore]);

  const handleActionClick = (action: string) => {
    // Handle quick actions
    switch (action) {
      case "update-os":
        console.log("Triggering OS update for device:", device.id);
        break;
      case "security-scan":
        console.log("Starting security scan for device:", device.id);
        break;
      case "battery-check":
        console.log("Checking battery health for device:", device.id);
        break;
      case "refresh":
        console.log("Refreshing device data for device:", device.id);
        // In real implementation, this would trigger a data refresh
        break;
      case "app-inventory":
        console.log("Opening app inventory for device:", device.id);
        break;
      default:
        console.log("Unknown action:", action);
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Health Score */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <HealthScoreCard 
            healthScore={healthScore} 
            trend={healthTrend}
            isLoadingTrend={isLoadingTrend}
          />
        </div>
        <div className="lg:col-span-2">
          <HealthInsightsPanel 
            healthScore={healthScore} 
            onActionClick={handleActionClick} 
          />
        </div>
      </div>

      {/* Detailed Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Health Metrics Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {healthScore.metrics.map((metric, index) => (
            <HealthMetricCard
              key={index}
              metric={metric}
              showRecommendations={false}
            />
          ))}
        </div>
      </div>

      {/* Device Information Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium text-sm text-muted-foreground">Device Type</h4>
          <p className="text-lg font-semibold">{device.os_type}</p>
        </div>
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium text-sm text-muted-foreground">Status</h4>
          <p className="text-lg font-semibold capitalize">{device.status}</p>
        </div>
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium text-sm text-muted-foreground">Last Seen</h4>
          <p className="text-lg font-semibold">
            {Math.floor((Date.now() - new Date(device.last_seen).getTime()) / (1000 * 60 * 60 * 24))} days ago
          </p>
        </div>
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium text-sm text-muted-foreground">Apps Installed</h4>
          <p className="text-lg font-semibold">{device.installed_apps?.length || 0}</p>
        </div>
      </div>
    </div>
  );
};

export default DeviceAnalytics;